import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../api';

export default function Prints() {
  const [prints, setPrints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({ designCode: '', designName: '', quantity: '' });
  const [updateForm, setUpdateForm] = useState({ designCode: '', quantity: '' });
  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [quickAddCode, setQuickAddCode] = useState(null);
  const [tab, setTab] = useState('inventory'); // 'inventory' | 'reorder'
  const [reorder, setReorder] = useState(null);
  const [buffer, setBuffer] = useState(0);
  const [forecast, setForecast] = useState(null);
  const [fDays, setFDays] = useState(3);
  const [fService, setFService] = useState(0.8);
  const [calibrating, setCalibrating] = useState(false);

  const fetchReorder = async () => {
    try { setReorder((await axios.get(`${API}/api/prints/reorder-needs?buffer=${buffer}`)).data); }
    catch (e) { console.error(e); }
  };

  const fetchForecast = async () => {
    try { setForecast((await axios.get(`${API}/api/prints/forecast?days=${fDays}&service=${fService}`)).data); }
    catch (e) { console.error(e); }
  };

  // Recalibration runs nightly on its own; this is for when you want it now.
  const handleCalibrate = async () => {
    setCalibrating(true);
    try {
      const res = await axios.post(`${API}/api/prints/forecast/calibrate?days=${fDays}&service=${fService}`);
      const d = res.data.designs || {};
      alert(
        `Recalibrated against actual sales.\n\n` +
        `${d.calibrated} design(s) tuned, ${d.skipped} skipped (too little history)` +
        (d.cannotReachTarget ? `, ${d.cannotReachTarget} cannot reach the target` : '') + `.\n` +
        `Half-life ${res.data.halfLifeDays} days · new-design rate ${res.data.launchRate}/day.`
      );
      await fetchForecast();
    } catch (e) {
      alert('Calibration failed: ' + (e.response?.data?.message || e.message));
    } finally { setCalibrating(false); }
  };

  const totalOnOrder = (reorder?.rows || []).reduce((s, r) => s + (r.onOrder || 0), 0);
  const forecastOnOrder = (forecast?.rows || []).reduce((s, r) => s + (r.onOrder || 0), 0);

  // Move every ordered print into on-shelf stock, from the Forecast tab.
  const handleReceiveForecast = async () => {
    if (!window.confirm(
      `Add all ${forecastOnOrder} ordered print(s) into on-shelf stock?\n\nUse this when the prints have physically arrived. Their "Ordered" counts reset to 0.`
    )) return;
    try {
      const res = await axios.post(`${API}/api/prints/receive-on-order`);
      alert(`✓ ${res.data.totalReceived} print(s) across ${res.data.designs} design(s) added to stock.`);
      await fetchForecast();
      await fetchPrints();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  // Only some designs usually arrive at once, so a print can be received on
  // its own without pretending the whole order landed.
  const handleReceiveOne = async (r) => {
    if (!r.onOrder) return;
    if (!window.confirm(`Add ${r.onOrder} ${r.designName} (${r.designCode}) print(s) into on-shelf stock?`)) return;
    try {
      const res = await axios.post(`${API}/api/prints/receive-on-order`, { designCodes: [r.designCode] });
      alert(`✓ ${res.data.totalReceived} added to stock.`);
      await fetchForecast();
      await fetchPrints();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  // Move every design's Ordered quantity into on-shelf stock (prints arrived)
  const handleReceiveOnOrder = async () => {
    if (!window.confirm(
      `Add all ${totalOnOrder} ordered print(s) into on-shelf stock?\n\nUse this when the ordered prints have physically arrived. Their "Ordered" counts reset to 0.`
    )) return;
    try {
      const res = await axios.post(`${API}/api/prints/receive-on-order`);
      alert(`✓ ${res.data.totalReceived} print(s) across ${res.data.designs} design(s) added to stock.`);
      await fetchReorder();
      await fetchPrints();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  // Whichever of the two list views is on screen. The other refetches by
  // itself when you switch to it.
  const refreshActive = async () => {
    if (tab === 'forecast') await fetchForecast();
    else await fetchReorder();
  };

  // Record how many prints of this design you've just ordered from the printer
  const handleAddOnOrder = async (r) => {
    const val = window.prompt(
      `How many ${r.designName} (${r.designCode}) prints did you order?\n\nAlready on order: ${r.onOrder || 0}. Enter the number just ordered (negative to correct).`,
      String(Math.max(r.orderQty, 0) || '')
    );
    if (val === null) return;
    const qty = parseInt(val, 10);
    if (!qty) return;
    try {
      await axios.post(`${API}/api/prints/add-on-order`, { designCode: r.designCode, qty });
      // Both tabs share this button, so refresh whichever one is being
      // looked at. Refreshing only Reorder Needs left the Forecast row
      // showing its old Ordered and Order Qty until you switched tabs.
      await refreshActive();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  const BLANKS = ['Black', 'Off White', 'Blue', 'Maroon', 'Beige', 'Pink'];
  const handleSetBlank = async (p) => {
    const val = window.prompt(
      `Blank colour for ${p.designName} (${p.designCode})\nCurrent: ${p.blankColor || 'not set'}\n\nType one of: ${BLANKS.join(' / ')}`,
      p.blankColor || ''
    );
    if (val === null || !val.trim()) return;
    const match = BLANKS.find(b => b.toLowerCase() === val.trim().toLowerCase());
    if (!match) { alert('Please type exactly one of: ' + BLANKS.join(', ')); return; }
    try {
      await axios.post(`${API}/api/prints/set-blank-color`, { designCode: p.designCode, blankColor: match });
      await fetchPrints();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleQuickAdd = async (p) => {
    const val = window.prompt(
      `Add stock for ${p.designName} (${p.designCode})\nCurrent quantity: ${p.quantity}\n\nHow many to add? (negative number subtracts)`
    );
    if (val === null || val.trim() === '') return;
    const qty = parseInt(val, 10);
    if (isNaN(qty) || qty === 0) { alert('Please enter a number'); return; }
    setQuickAddCode(p.designCode);
    try {
      await axios.post(`${API}/api/prints/update`, { designCode: p.designCode, quantity: qty });
      await fetchPrints();
    } catch (e) {
      alert('Update failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setQuickAddCode(null);
    }
  };

  useEffect(() => {
    fetchPrints();
  }, []);

  useEffect(() => {
    if (tab === 'reorder') fetchReorder();
    if (tab === 'forecast') fetchForecast();
    // eslint-disable-next-line
  }, [tab, buffer]);

  const fetchPrints = async () => {
    try {
      const res = await axios.get(`${API}/api/prints/with-velocity`);
      setPrints(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await axios.post(`${API}/api/prints/add`, {
        designCode: form.designCode.toUpperCase(),
        designName: form.designName,
        quantity: parseInt(form.quantity)
      });
      await fetchPrints();
      setForm({ designCode: '', designName: '', quantity: '' });
    } catch (e) {
      alert('Error adding design — code may already exist');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      await axios.post(`${API}/api/prints/update`, {
        designCode: updateForm.designCode.toUpperCase(),
        quantity: parseInt(updateForm.quantity)
      });
      await fetchPrints();
      setUpdateForm({ designCode: '', quantity: '' });
    } catch (e) {
      alert('Design code not found');
    } finally {
      setUpdating(false);
    }
  };

  const filtered = prints.filter(p => {
    const matchSearch = p.designName.toLowerCase().includes(search.toLowerCase()) ||
      p.designCode.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' ? true :
      filter === 'instock' ? p.quantity > 0 :
      filter === 'outofstock' ? p.quantity === 0 :
      p.quantity <= p.reorderPoint;
    return matchSearch && matchFilter;
  });

  const totalDesigns = prints.length;
  const inStock = prints.filter(p => p.quantity > 0).length;
  const outOfStock = prints.filter(p => p.quantity === 0).length;
  const totalPrints = prints.reduce((sum, p) => sum + p.quantity, 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[['inventory', 'Prints Inventory'], ['reorder', 'Reorder Needs'], ['forecast', 'Forecast Order']].map(([v, label]) => (
          <button key={v} onClick={() => setTab(v)} style={{
            padding: '9px 18px', fontSize: '14px', borderRadius: '8px', cursor: 'pointer',
            background: tab === v ? '#6e1f2a' : '#ffffff', color: tab === v ? '#fff' : '#7d6a63',
            border: `1px solid ${tab === v ? '#6e1f2a' : '#e6dccc'}`
          }}>{label}</button>
        ))}
      </div>

      {tab === 'reorder' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div className="card-title" style={{ margin: 0 }}>Reorder Needs — designs that go negative once every pending order ships</div>
            {reorder && totalOnOrder > 0 && (
              <button
                className="btn-primary"
                onClick={handleReceiveOnOrder}
                title="Move every design's Ordered quantity into on-shelf stock (use when the ordered prints arrive)"
                style={{ fontSize: '13px', padding: '9px 16px' }}
              >
                ⬆ Publish ordered → stock ({totalOnOrder})
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '14px', marginTop: '14px', flexWrap: 'wrap' }}>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '4px' }}>Safety buffer (extra per design)</div>
              <input type="number" value={buffer} onChange={e => setBuffer(parseInt(e.target.value) || 0)} style={{ width: '110px' }} /></div>
          </div>
          <p style={{ fontSize: '12px', color: '#93827a', marginBottom: '14px' }}>
            Counts demand from ALL pending orders (including greyed-out / out-of-stock ones). Returns-inventory pieces are netted out first (they don't consume fresh prints). Screenshot the Order Qty column for your print order.
          </p>
          {!reorder ? <p style={{ color: '#93827a' }}>Loading...</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr><th>Code</th><th>Design</th><th style={{ color: '#7a6a12' }}>Order Qty</th><th>Ordered</th><th>In Stock</th><th>Pending Demand</th><th>Ends At</th></tr>
                </thead>
                <tbody>
                  {reorder.rows.map(r => (
                    <tr key={r.designCode}>
                      <td style={{ fontFamily: 'monospace', color: '#8c3242' }}>{r.designCode}</td>
                      <td style={{ fontWeight: 500 }}>{r.designName}</td>
                      <td style={{ fontWeight: 700, color: r.orderQty > 0 ? '#7a6a12' : '#b0a296', fontSize: '15px' }}>{r.orderQty || '—'}</td>
                      <td>
                        <span style={{ color: r.onOrder > 0 ? '#2e7d50' : '#93827a', fontWeight: 600, marginRight: '8px' }}>{r.onOrder || 0}</span>
                        <button
                          onClick={() => handleAddOnOrder(r)}
                          title="Record prints you've ordered from the printer"
                          style={{ fontSize: '13px', lineHeight: 1, padding: '2px 9px', background: '#ffffff', color: '#2e7d50', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}
                        >+</button>
                      </td>
                      <td style={{ color: r.currentStock <= 0 ? '#b3261e' : '#2e1f23' }}>{r.currentStock}</td>
                      <td>{r.pendingDemand}</td>
                      <td style={{ color: r.projected < 0 ? '#b3261e' : '#2e7d50', fontWeight: 600 }}>{r.projected}</td>
                    </tr>
                  ))}
                  {reorder.rows.length === 0 && (
                    <tr><td colSpan="7" style={{ color: '#2e7d50', textAlign: 'center' }}>No design goes negative — all pending orders are covered 🎉</td></tr>
                  )}
                </tbody>
              </table>
              {reorder.rows.length > 0 && (
                <div style={{ marginTop: '14px', color: '#4a3840', fontSize: '14px' }}>
                  <b>{reorder.count}</b> design(s) to reorder · total <b style={{ color: '#7a6a12' }}>{reorder.totalToOrder}</b> prints
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'forecast' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div className="card-title" style={{ margin: 0 }}>Forecast Order — how many prints to buy to cover the next {fDays} days</div>
            <div style={{ display: 'flex', gap: '8px' }}>
            {forecastOnOrder > 0 && (
              <button className="btn-primary" onClick={handleReceiveForecast}
                title="Move every design's Ordered quantity into on-shelf stock (use when the prints arrive)"
                style={{ fontSize: '13px', padding: '9px 16px' }}>
                ⬆ Publish ordered → stock ({forecastOnOrder})
              </button>
            )}
            <button onClick={handleCalibrate} disabled={calibrating}
              title="Re-measure the model against what actually sold. Runs automatically every night."
              style={{ fontSize: '13px', padding: '9px 16px', borderRadius: '8px', cursor: calibrating ? 'wait' : 'pointer',
                background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc' }}>
              {calibrating ? 'Recalibrating…' : '↻ Recalibrate now'}
            </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', margin: '14px 0', flexWrap: 'wrap' }}>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '4px' }}>Days to cover</div>
              <input type="number" min="1" max="30" value={fDays}
                onChange={e => setFDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
                onBlur={fetchForecast} style={{ width: '90px' }} /></div>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '4px' }}>Spare stock</div>
              <select value={fService} onChange={e => { setFService(parseFloat(e.target.value)); }} onBlur={fetchForecast} style={{ width: '210px' }}>
                <option value={0.5}>None — just the likely demand</option>
                <option value={0.8}>A few spare (recommended)</option>
                <option value={0.95}>Plenty spare</option>
              </select></div>
            <button className="btn-primary" onClick={fetchForecast} style={{ fontSize: '13px', padding: '9px 16px' }}>Apply</button>
          </div>

          <p style={{ fontSize: '12px', color: '#93827a', marginBottom: '14px' }}>
            Only designs <b>selling right now</b> get stock ordered ahead — enough for roughly {fDays} days at the rate they
            are currently going, plus a little spare so you aren't caught out. Everything else is ordered only for orders
            actually placed. Whatever you already hold — on shelf, on order, and returns back on the shelf — comes off first.
          </p>

          {!forecast ? <p style={{ color: '#93827a' }}>Loading...</p> : (<>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '14px', fontSize: '13px', color: '#7d6a63' }}>
              <span>Order total <b style={{ color: '#7a6a12', fontSize: '15px' }}>{forecast.totalPrintsToOrder}</b> prints</span>
              <span>across <b style={{ color: '#2e1f23' }}>{forecast.rows.filter(r => r.orderNow > 0).length}</b> design(s)</span>
              <span title={forecast.moverRule}>selling now <b style={{ color: '#2e7d50' }}>{forecast.movingNow}</b></span>
              <span title="The model re-measures itself against actual sales every night.">last tuned <b style={{ color: '#2e1f23' }}>{forecast.calibratedAt ? new Date(forecast.calibratedAt).toLocaleString() : 'never'}</b></span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr><th>Code</th><th>Design</th><th style={{ color: '#7a6a12' }}>Order Qty</th><th>Ordered</th>
                    <th>Have</th><th>Committed</th><th>Spare {fDays}d</th><th>Rate/day</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {forecast.rows.map(r => (
                    <tr key={r.designCode}>
                      <td style={{ fontFamily: 'monospace', color: '#8c3242' }}>{r.designCode}</td>
                      <td style={{ fontWeight: 500 }}>{r.designName}</td>
                      <td style={{ fontWeight: 700, color: r.orderNow > 0 ? '#7a6a12' : '#b0a296', fontSize: '15px' }}>{r.orderNow || '—'}</td>
                      <td>
                        <span style={{ color: r.onOrder > 0 ? '#2e7d50' : '#93827a', fontWeight: 600, marginRight: '8px' }}>{r.onOrder || 0}</span>
                        <button
                          onClick={() => handleAddOnOrder({ ...r, orderQty: r.orderNow })}
                          title="Record prints you've ordered from the printer"
                          style={{ fontSize: '13px', lineHeight: 1, padding: '2px 9px', background: '#ffffff', color: '#2e7d50', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}
                        >+</button>
                        {r.onOrder > 0 && (
                          <button
                            onClick={() => handleReceiveOne(r)}
                            title={`These ${r.onOrder} have arrived — move them onto the shelf`}
                            style={{ fontSize: '13px', lineHeight: 1, padding: '2px 9px', marginLeft: '5px', background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}
                          >⬆</button>
                        )}
                      </td>
                      <td title={`${r.stock} on shelf + ${r.onOrder} on order + ${r.restockedReturns} returns`}
                        style={{ color: r.have <= 0 ? '#b3261e' : '#2e1f23' }}>{r.have}</td>
                      <td>{r.committed || '—'}</td>
                      <td title={`expected ${r.expected}`}>{r.forecastCover}</td>
                      <td style={{ color: '#7d6a63' }}>{r.dailyRate ?? '—'}</td>
                      <td style={{ fontSize: '12px' }}>
                        {r.running
                          ? <span style={{ color: '#2e7d50' }} title={`${r.recentUnits} sold recently, last one ${r.quietDays} day(s) ago. ${r.calibrated ? `Tuned on ${r.calibrationWindows} past ${fDays}-day windows.` : 'Not tuned yet — using the plain model.'}`}>
                              selling ({r.recentUnits})
                            </span>
                          : r.committed > 0
                            ? <span style={{ color: '#8c3242' }} title="Not selling ahead — this is only covering orders already placed.">pending orders only</span>
                            : <span style={{ color: '#93827a' }} title={r.quietDays === null ? 'No sales in the window.' : `No sale for ${r.quietDays} days.`}>quiet</span>}
                      </td>
                    </tr>
                  ))}
                  {forecast.rows.length === 0 && (
                    <tr><td colSpan="9" style={{ color: '#2e7d50', textAlign: 'center' }}>Nothing in demand — no prints needed 🎉</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>)}
        </div>
      )}

      {tab === 'inventory' && (<>
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Total Designs</div>
          <div className="stat-value">{totalDesigns}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">In Stock</div>
          <div className="stat-value" style={{ color: '#2e7d50' }}>{inStock}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Out of Stock</div>
          <div className="stat-value" style={{ color: '#b3261e' }}>{outOfStock}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Print Units</div>
          <div className="stat-value">{totalPrints}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Prints Inventory</div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            placeholder="Search design name or code..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '280px' }}
          />
          <select value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All Designs</option>
            <option value="instock">In Stock</option>
            <option value="outofstock">Out of Stock</option>
            <option value="low">Low Stock</option>
          </select>
        </div>

        {loading ? <p style={{ color: '#93827a' }}>Loading...</p> : (
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Design Name</th>
                <th>Blank</th>
                <th>Velocity</th>
                <th>Quantity</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p._id}>
                  <td style={{ fontFamily: 'monospace', color: '#8c3242' }}>{p.designCode}</td>
                  <td style={{ fontWeight: '500' }}>{p.designName}</td>
                  <td>
                    <span
                      onClick={() => handleSetBlank(p)}
                      title="Click to change the blank colour this design prints on"
                      style={{
                        cursor: 'pointer', fontSize: '12px', padding: '3px 10px', borderRadius: '20px',
                        background: p.blankColor ? '#f8f3eb' : '#fbe9e7',
                        border: '1px solid ' + (p.blankColor ? '#e6dccc' : '#8c1d18'),
                        color: p.blankColor ? '#2e1f23' : '#b3261e'
                      }}
                    >
                      {p.blankColor || 'set blank ⚠'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      color: p.velocity >= 2 ? '#b3261e' : p.velocity >= 1 ? '#7a6a12' : p.velocity > 0 ? '#2e7d50' : '#93827a',
                      fontWeight: '600'
                    }}>
                      {p.velocity}/day
                    </span>
                  </td>
                  <td style={{ fontWeight: '600' }}>
                    {p.quantity}
                    <button
                      onClick={() => handleQuickAdd(p)}
                      disabled={quickAddCode === p.designCode}
                      title="Add or subtract stock for this design"
                      style={{
                        marginLeft: '10px',
                        fontSize: '12px',
                        padding: '2px 9px',
                        background: '#ffffff',
                        color: '#8c3242',
                        border: '1px solid #e6dccc',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      {quickAddCode === p.designCode ? '…' : '±'}
                    </button>
                  </td>
                  <td>
                    {p.quantity === 0
                      ? <span className="badge badge-red">Out of Stock</span>
                      : p.quantity <= p.reorderPoint
                      ? <span className="badge badge-yellow">Low Stock</span>
                      : <span className="badge badge-green">In Stock</span>
                    }
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="6" style={{ color: '#93827a', textAlign: 'center' }}>No designs found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div className="card">
          <div className="card-title">Add New Design</div>
          <form onSubmit={handleAdd} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Design Code</div>
              <input
                placeholder="e.g. NEW001"
                value={form.designCode}
                onChange={e => setForm({ ...form, designCode: e.target.value })}
                style={{ width: '100%' }}
                required
              />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Design Name</div>
              <input
                placeholder="e.g. New Design"
                value={form.designName}
                onChange={e => setForm({ ...form, designName: e.target.value })}
                style={{ width: '100%' }}
                required
              />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Initial Quantity</div>
              <input
                type="number"
                placeholder="0"
                value={form.quantity}
                onChange={e => setForm({ ...form, quantity: e.target.value })}
                style={{ width: '100%' }}
                required
              />
            </div>
            <button type="submit" className="btn-primary" disabled={adding}>
              {adding ? 'Adding...' : 'Add Design'}
            </button>
          </form>
        </div>

        <div className="card">
          <div className="card-title">Update Print Stock</div>
          <form onSubmit={handleUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Design Code</div>
              <input
                placeholder="e.g. KJ001"
                value={updateForm.designCode}
                onChange={e => setUpdateForm({ ...updateForm, designCode: e.target.value })}
                style={{ width: '100%' }}
                required
              />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Add Quantity</div>
              <input
                type="number"
                placeholder="Units to add"
                value={updateForm.quantity}
                onChange={e => setUpdateForm({ ...updateForm, quantity: e.target.value })}
                style={{ width: '100%' }}
                required
              />
            </div>
            <button type="submit" className="btn-primary" disabled={updating}>
              {updating ? 'Updating...' : 'Add Stock'}
            </button>
          </form>
        </div>
      </div>
      </>)}
    </div>
  );
}